import {RefractoryClassificationClass} from '@domain/refractory-product-catalog'
import {useUpmPlant} from '@hconnect/upm'
import {createContext, useContext, useMemo, useState} from 'react'
import {useForm, UseFormReturn, useWatch} from 'react-hook-form'

import {useAddStaticPartReplacement, useEditStaticPartReplacement} from '../hooks/mutations/'

import {useRefractoriesOptions} from '@refra/modules/common/hooks/queries'
import {useShutdowns} from '@refra/modules/common/providers/ShutdownsProvider'
import type {Refractory} from '@refra/modules/refra-catalog/types'
import {
  StaticPartEditModesEnum,
  StaticPartInteractionModesEnum,
  StaticPartLayerTypes
} from '@refra/modules/refra-static-parts/enums'
import {
  LayerFormDTO,
  ReplacementFormDTO,
  StaticPartComponentDto,
  StaticReplacementDTO
} from '@refra/modules/refra-static-parts/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface EditReplacementContext {
  editMode: StaticPartEditModesEnum
  setEditMode: (value: StaticPartEditModesEnum) => void
  interactionMode: StaticPartInteractionModesEnum
  setInteractionMode: (value: StaticPartInteractionModesEnum) => void
  activeMaterialInstallationIndex: number
  setActiveMaterialInstallationIndex: (index: number) => void
  methods: UseFormReturn<ReplacementFormDTO>
  replacementAreas: number[]
  totalAreaSelected: number
  materialWeights: number[][]
  activeAfter?: string
  assignedRefractoriesMap: Record<number, Refractory>
  isSavingReplacement: boolean
  onSubmit: () => void
  cancelChanges: () => void
}

const EditReplacementContext = createContext<EditReplacementContext | undefined>(undefined)

export const useEditReplacement = () => {
  const context = useContext(EditReplacementContext)
  if (!context)
    throw new Error('useEditReplacement is used outside of the EditReplacementProvider!')
  return context
}

interface EditReplacementProviderProps {
  selectedComponent: StaticPartComponentDto
  replacement?: StaticReplacementDTO
  children: React.ReactNode
}

export const DEFAULT_LAYERS: LayerFormDTO[] = Object.values(StaticPartLayerTypes).map((layer) => ({
  layerType: layer,
  bricksInfo: {}
}))

export const EditReplacementProvider: React.FC<EditReplacementProviderProps> = ({
  children,
  replacement,
  selectedComponent
}) => {
  const plantCode = useUrlParam('plantCode')
  const selectedKilnId = useUrlParam('selectedKilnId')
  const shutdownId = useUrlParam('shutdownId')
  const upmPlant = useUpmPlant({plantId: plantCode})
  const {activeShutdowns} = useShutdowns()
  const shutdownInfo = activeShutdowns.find((shutdown) => shutdown.id === Number(shutdownId))
  const activeAfter = shutdownInfo?.interventionDate

  const [editMode, setEditMode] = useState(StaticPartEditModesEnum.ADD)
  const [interactionMode, setInteractionMode] = useState(StaticPartInteractionModesEnum.SELECT)
  const [activeMaterialInstallationIndex, setActiveMaterialInstallationIndex] = useState(0)

  const {mutate: addStaticPartReplacement, isPending: isAddingStaticPartReplacement} =
    useAddStaticPartReplacement()
  const {mutate: editStaticPartReplacement, isPending: isEditingStaticPartReplacement} =
    useEditStaticPartReplacement()

  const defaultMaterialInstallations: ReplacementFormDTO = useMemo(() => {
    if (!replacement) {
      return {
        materialInstallations: [
          {
            sectionIds: [],
            layers: DEFAULT_LAYERS
          }
        ]
      }
    }

    return {
      materialInstallations: replacement.materialInstallations.map((mi) => {
        const sectionIds: number[] = mi.sections?.map((s) => s.id) ?? []
        const layers: LayerFormDTO[] = DEFAULT_LAYERS.map((defaultLayer) => {
          const existingLayer = mi.layers!.find(
            (layerInfo) => layerInfo.layer === defaultLayer.layerType
          )
          if (existingLayer) {
            return {
              refractoryId: existingLayer.refractoryId,
              layerType: defaultLayer.layerType,
              bricksInfo: existingLayer.bricksInfo ?? {}
            }
          }
          return defaultLayer
        })

        return {
          id: mi.id,
          sectionIds,
          layers
        }
      })
    }
  }, [replacement])

  const methods = useForm<ReplacementFormDTO>({
    defaultValues: defaultMaterialInstallations,
    values: defaultMaterialInstallations
  })

  const watchReplacements = useWatch({
    control: methods.control,
    name: 'materialInstallations'
  })

  const assignedRefractoryIds = useMemo(() => {
    const ids = new Set<number>()
    watchReplacements.forEach((replacement) =>
      replacement?.layers?.forEach((layer) => {
        if (layer.refractoryId !== undefined && layer.refractoryId !== null) {
          ids.add(layer.refractoryId)
        }
      })
    )
    return Array.from(ids)
  }, [watchReplacements])

  const {refractoriesMap: assignedRefractoriesMap} = useRefractoriesOptions({
    ids: assignedRefractoryIds,
    enabled: assignedRefractoryIds.length > 0
  })

  const replacementAreas = useMemo(() => {
    return watchReplacements.map((replacement) => {
      const sectionIds = replacement?.sectionIds
      if (!sectionIds) return 0
      return sectionIds.reduce((acc: number, sectionId) => {
        const section = selectedComponent.sections.find((s) => s.id === Number(sectionId))
        if (section) {
          acc += section.area
        }
        return acc
      }, 0)
    })
  }, [watchReplacements, selectedComponent.sections])

  const totalAreaSelected = useMemo(() => {
    return replacementAreas.reduce((total, area) => total + area, 0)
  }, [replacementAreas])

  const materialWeights = useMemo(() => {
    return watchReplacements.map((replacement, index) => {
      const area = replacementAreas[index] || 0

      return (replacement?.layers || []).map((layer) => {
        const thickness = layer.bricksInfo?.thickness
        const refractoryId = layer.refractoryId
        const selectedRefractory =
          refractoryId !== undefined && refractoryId !== null
            ? assignedRefractoriesMap[refractoryId]
            : undefined
        const isBrick =
          selectedRefractory?.classification?.class === RefractoryClassificationClass.Brick

        if (!selectedRefractory || isBrick || !thickness) return 0

        const densityArray = selectedRefractory.physicalData?.BulkDensity
        if (!densityArray || densityArray.length === 0) return 0
        const density = densityArray[0].metric ?? 0
        const thicknessMeters = thickness / 1000
        return Number((thicknessMeters * density * area).toFixed(2))
      })
    })
  }, [watchReplacements, replacementAreas, assignedRefractoriesMap])

  const onSubmit = methods.handleSubmit((data) => {
    const allInstallations = data.materialInstallations
    const validInstallations = allInstallations.filter((mi) => mi.sectionIds.length > 0)

    if (validInstallations.length < allInstallations.length) {
      void methods.trigger()
      return
    }

    const finalReplacementDTO = {
      plantUpmId: upmPlant.upmId,
      shutdownId: Number(shutdownId),
      kilnUpmId: selectedKilnId,
      staticPartReplacementDTO: {
        componentId: selectedComponent.id,
        materialInstallations: validInstallations.map((mi) => ({
          sectionIds: mi.sectionIds,
          layers: mi.layers
            .filter((layer) => !!layer.refractoryId)
            .map((layer) => ({
              refractoryId: layer.refractoryId,
              layer: layer.layerType,
              bricksInfo: layer.bricksInfo
            }))
        }))
      }
    }

    replacement?.id
      ? editStaticPartReplacement({
          ...finalReplacementDTO,
          replacementId: replacement.id
        })
      : addStaticPartReplacement(finalReplacementDTO)
  })

  const cancelChanges = () => {
    methods.reset(defaultMaterialInstallations)
  }

  const isSavingReplacement = isAddingStaticPartReplacement || isEditingStaticPartReplacement

  return (
    <EditReplacementContext.Provider
      value={{
        editMode,
        setEditMode,
        interactionMode,
        setInteractionMode,
        activeMaterialInstallationIndex: activeMaterialInstallationIndex,
        setActiveMaterialInstallationIndex: setActiveMaterialInstallationIndex,
        methods,
        replacementAreas,
        totalAreaSelected,
        materialWeights,
        activeAfter,
        assignedRefractoriesMap,
        isSavingReplacement,
        onSubmit,
        cancelChanges
      }}
    >
      {children}
    </EditReplacementContext.Provider>
  )
}
